import { BED_JOINT_GAP_MM, COORDINATE_QUANTUM_MM, EPSILON } from './constants.js';
import type { BrickKind } from './catalogue.js';
import { BRICK_SPECS, effectiveSize } from './catalogue.js';
import type { PlacedBrick, BrickId } from './brick.js';
import { DEFAULT_MATERIAL_ID } from './brick.js';
import type { SketchShape } from './sketch.js';
import { rectInsidePolygon, rectOverlapsPolygon, shapeBounds } from './sketch.js';
import type { WallModel } from './wall-model.js';
import type { MaterialId, Mm, Rotation } from './units.js';
import { quantise } from './units.js';
import { fail } from './errors.js';

/**
 * Design document C4 — the bond generator.
 *
 * Output is ORDINARY BRICKS, not a live parametric object: there is one kind
 * of thing in the document, and everything the generator lays can afterwards
 * be moved, painted or deleted by hand.
 */
export type BondKind = 'stretcher' | 'english' | 'flemish' | 'header' | 'stack' | 'soldier';

export const BOND_KINDS: readonly BondKind[] = [
  'stretcher', 'english', 'flemish', 'header', 'stack', 'soldier',
];

export const BOND_LABELS: Record<BondKind, string> = {
  stretcher: 'Stretcher (running)',
  english: 'English',
  flemish: 'Flemish',
  header: 'Header',
  stack: 'Stack',
  soldier: 'Soldier course',
};

/** How the last unit in a course is resolved when it overhangs the envelope. */
export type EndTreatment = 'trim' | 'overhang' | 'cut';

/** The unit that closes a quoin on alternate courses. */
export type Closer = 'header' | 'three_quarter_bat';

export interface Envelope {
  readonly x: Mm;
  readonly y: Mm;
  readonly w: Mm;
  readonly h: Mm;
}

/**
 * Where one course starts and stops, overriding the envelope's own width.
 *
 * This is how a wall is set out from its corners. At a quoin the courses
 * alternate between running through the return and stopping short of it, and
 * that alternation has to be decided BEFORE the units are laid rather than
 * trimmed in afterwards: half a wall thickness is always exactly five
 * millimetres more than a catalogue unit, so a course cut back to a return
 * line leaves a gap nothing in the catalogue fits.
 *
 * Laid from the return line the course comes out exact instead — provided the
 * length it then has is a whole number of units. That is a condition on the
 * DIMENSIONS, not something this can guarantee: it holds when the design
 * length and the wall thickness plus a joint are both whole numbers of the
 * bond's unit module. `shortfallXMm` reports the worst course that failed to
 * reach, so a caller can tell whether the dimensions it chose work.
 *
 * Nothing here knows what a junction is. The caller works that out and hands
 * down two numbers per course.
 */
export type CourseSpan = (y: Mm) => { readonly from: Mm; readonly to: Mm };

export interface BondRequest {
  readonly envelope: Envelope;
  readonly bond: BondKind;
  readonly perpendGapMm: Mm;
  readonly endTreatment?: EndTreatment;
  readonly closer?: Closer;
  readonly materialId?: MaterialId;
  readonly courseSpan?: CourseSpan | undefined;
}

export interface PlannedBrick {
  readonly kind: BrickKind;
  readonly x: Mm;
  readonly y: Mm;
  readonly rotation: Rotation;
  readonly materialId: MaterialId;
  readonly width: Mm;
  readonly height: Mm;
}

export interface BondPlan {
  readonly bricks: readonly PlannedBrick[];
  readonly courses: number;
  /** Widest gap left at a course end, in millimetres. */
  readonly shortfallXMm: Mm;
  /** Height left above the top course. */
  readonly shortfallYMm: Mm;
  readonly bond: BondKind;
  readonly envelope: Envelope;
}

interface CourseRecipe {
  readonly lead: readonly BrickKind[];
  readonly repeat: readonly BrickKind[];
  readonly rotation: Rotation;
}

function recipe(bond: BondKind, course: number, closer: Closer): CourseRecipe {
  const odd = course % 2 === 1;
  switch (bond) {
    case 'stretcher':
      // Alternate courses start with a half brick, which is how a quoin is
      // actually built and what produces the half bond at a 10 mm joint.
      return { lead: odd ? [closer] : [], repeat: ['stretcher'], rotation: 0 };
    case 'english':
      return odd
        ? { lead: ['header', 'queenie'], repeat: ['header'], rotation: 0 }
        : { lead: [], repeat: ['stretcher'], rotation: 0 };
    case 'flemish':
      return odd
        ? { lead: ['header', 'queenie'], repeat: ['stretcher', 'header'], rotation: 0 }
        : { lead: [], repeat: ['stretcher', 'header'], rotation: 0 };
    case 'header':
      return { lead: odd ? ['queenie'] : [], repeat: ['header'], rotation: 0 };
    case 'stack':
      return { lead: [], repeat: ['stretcher'], rotation: 0 };
    default:
      return { lead: [], repeat: ['stretcher'], rotation: 90 };
  }
}

/** The largest catalogue unit that fits a remaining width, if any. */
function largestFitting(width: Mm, rotation: Rotation): BrickKind | null {
  let best: BrickKind | null = null;
  let bestWidth = 0;
  for (const spec of Object.values(BRICK_SPECS)) {
    const [w] = effectiveSize(spec.key, rotation);
    if (w <= width + EPSILON && w > bestWidth) {
      best = spec.key;
      bestWidth = w;
    }
  }
  return best;
}

/**
 * Lay a named bond into a rectangle. Pure: it plans, it does not place.
 * Courses sit at `envelope.y + n * (unitHeight + bedJoint)`, and units run
 * left to right at the LIVE perpend gap.
 */
export function planBond(request: BondRequest): BondPlan {
  const { envelope, bond, perpendGapMm } = request;
  const endTreatment = request.endTreatment ?? 'trim';
  const closer = request.closer ?? 'header';
  const materialId = request.materialId ?? DEFAULT_MATERIAL_ID;
  if (!(envelope.w > 0) || !(envelope.h > 0)) fail('The envelope needs a positive width and height');
  if (!Number.isFinite(envelope.x) || !Number.isFinite(envelope.y)) {
    fail('The envelope origin must be finite');
  }

  const rotation: Rotation = bond === 'soldier' ? 90 : 0;
  const [, unitHeight] = effectiveSize('stretcher', rotation);
  const pitch = unitHeight + BED_JOINT_GAP_MM;
  const courses = Math.max(0, Math.floor((envelope.h + BED_JOINT_GAP_MM) / pitch));

  const bricks: PlannedBrick[] = [];
  let shortfallXMm = 0;

  for (let course = 0; course < courses; course++) {
    const y = quantise(envelope.y + course * pitch);
    const span = request.courseSpan?.(y);
    const left = span === undefined ? envelope.x : span.from;
    const right = span === undefined ? envelope.x + envelope.w : span.to;
    if (right - left <= 0) continue;
    const { lead, repeat, rotation: courseRotation } = recipe(bond, course, closer);
    let x = left;
    let step = 0;

    for (;;) {
      const kind =
        step < lead.length
          ? (lead[step] as BrickKind)
          : (repeat[(step - lead.length) % repeat.length] as BrickKind);
      const [width, height] = effectiveSize(kind, courseRotation);

      if (x + width > right + EPSILON) {
        if (endTreatment === 'overhang') {
          bricks.push({ kind, x: quantise(x), y, rotation: courseRotation, materialId, width, height });
          x += width + perpendGapMm;
        } else if (endTreatment === 'cut') {
          const fitted = largestFitting(right - x, courseRotation);
          if (fitted !== null) {
            const [fw, fh] = effectiveSize(fitted, courseRotation);
            bricks.push({ kind: fitted, x: quantise(x), y, rotation: courseRotation, materialId, width: fw, height: fh });
            x += fw + perpendGapMm;
          }
        }
        break;
      }

      bricks.push({ kind, x: quantise(x), y, rotation: courseRotation, materialId, width, height });
      x += width + perpendGapMm;
      step += 1;
      if (step > 100_000) fail('The envelope is too large to fill');
    }

    // The gap left at this course's end, ignoring the joint that would follow.
    const remainder = Math.max(0, right - (x - perpendGapMm));
    shortfallXMm = Math.max(shortfallXMm, remainder);
  }

  const topCourseTop = courses === 0 ? envelope.y : envelope.y + (courses - 1) * pitch + unitHeight;
  return {
    bricks,
    courses,
    shortfallXMm: quantise(shortfallXMm),
    shortfallYMm: quantise(Math.max(0, envelope.y + envelope.h - topCourseTop)),
    bond,
    envelope,
  };
}

export interface BondValidation {
  /** Bricks that can be laid, in order. */
  readonly placeable: readonly PlannedBrick[];
  /** Bricks that meet existing work and will be skipped. */
  readonly clashing: readonly PlannedBrick[];
}

/**
 * Split a plan against the wall as it stands, so a bond can be generated
 * AROUND existing work rather than refusing to run.
 */
export function validateBondPlan(model: WallModel, plan: BondPlan): BondValidation {
  const placeable: PlannedBrick[] = [];
  const clashing: PlannedBrick[] = [];
  // Track what this run has already committed to, so members are checked
  // against each other as well as against the wall.
  const laid: PlannedBrick[] = [];
  for (const brick of plan.bricks) {
    const clearOfWall = model.canPlace(brick.x, brick.y, brick.width, brick.height);
    const clearOfRun = laid.every((other) => separated(brick, other, model.perpendGapMm));
    if (clearOfWall && clearOfRun) {
      placeable.push(brick);
      laid.push(brick);
    } else {
      clashing.push(brick);
    }
  }
  return { placeable, clashing };
}

function separated(a: PlannedBrick, b: PlannedBrick, perpendGapMm: Mm): boolean {
  const separatedX =
    a.x + a.width <= b.x - perpendGapMm + EPSILON || a.x >= b.x + b.width + perpendGapMm - EPSILON;
  const separatedY =
    a.y + a.height <= b.y - BED_JOINT_GAP_MM + EPSILON ||
    a.y >= b.y + b.height + BED_JOINT_GAP_MM - EPSILON;
  return separatedX || separatedY;
}

export interface BondResult {
  readonly placed: readonly PlacedBrick[];
  readonly skipped: number;
}

/** Commit a validated plan. The caller checkpoints; this is one action. */
export function applyBondPlan(model: WallModel, plan: BondPlan): BondResult {
  const { placeable, clashing } = validateBondPlan(model, plan);
  const placed = placeable.map((brick) =>
    model.addBrick(brick.kind, brick.x, brick.y, {
      rotation: brick.rotation,
      materialId: brick.materialId,
      validate: false,
    }),
  );
  return { placed, skipped: clashing.length };
}

/**
 * Fill a closed sketch shape rather than a rectangle.
 *
 * Courses run across the shape's extent; a unit is kept only when its whole
 * footprint lies inside the outline and clear of every opening. That is what
 * lets an arbitrary drawn wall — gable, buttress, anything — be laid
 * automatically (sketch mode).
 */
export function planBondInShape(
  shape: SketchShape,
  openings: readonly SketchShape[],
  request: Omit<BondRequest, 'envelope'>,
): BondPlan {
  const bounds = shapeBounds(shape);
  const envelope: Envelope = {
    x: bounds.minX,
    y: bounds.minY,
    w: bounds.maxX - bounds.minX,
    h: bounds.maxY - bounds.minY,
  };
  // Plan across the bounding box with an overhang, then keep what fits: a
  // trimmed course would stop at the box edge and miss a sloping outline.
  const gross = planBond({ ...request, envelope, endTreatment: 'overhang' });
  const kept = gross.bricks.filter((brick) => {
    const span = request.courseSpan?.(brick.y);
    if (span === undefined) {
      // No returns: the outline is the whole truth, and a unit that overhangs
      // it is the one the generator adds to be trimmed.
      if (!rectInsidePolygon(shape.points, brick.x, brick.y, brick.width, brick.height)) {
        return false;
      }
    } else {
      // A course set out from a bonded corner deliberately runs PAST the drawn
      // outline at the wall's ends, and that overhang IS the corner. The span
      // bounds such a course, so the extra unit beyond it is still dropped;
      // containment is then tested on the part of the brick that lies over the
      // outline, so a return brick is kept when the wall is solid at its
      // height rather than rejected for sticking out.
      if (brick.x < span.from - EPSILON) return false;
      if (brick.x + brick.width > span.to + EPSILON) return false;
      const from = clamp(brick.x, bounds.minX, bounds.maxX);
      const to = clamp(brick.x + brick.width, bounds.minX, bounds.maxX);
      const width = Math.max(to - from, COORDINATE_QUANTUM_MM);
      const x = Math.min(from, bounds.maxX - width);
      if (!rectInsidePolygon(shape.points, x, brick.y, width, brick.height)) return false;
    }
    return openings.every(
      (opening) => !rectOverlapsPolygon(opening.points, brick.x, brick.y, brick.width, brick.height),
    );
  });
  return { ...gross, bricks: kept, envelope };
}

const clamp = (value: Mm, low: Mm, high: Mm): Mm => Math.min(Math.max(value, low), high);

export type { BrickId };
